import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../data/cloud-data.js';
import { Configuration, AccentPackOptions } from '../../data/configuration.js';
import { ExteriorConfigurator } from '../../configurator/exterior-configurator.js';
import { VehicleView } from '../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../view-content.js';
import { CameraController, CameraViews } from '../../camera-controller.js';
import SelectedSectionItem from '../ui/selected-section-item.tsx';
import ViewContentRows from '../ui/view-content-rows.tsx';
import { AppContext } from '../app.tsx';


//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface FinalFinishesViewProps {
	exteriorConfigurator: ExteriorConfigurator;
	onBackButtonClicked: () => void;
	cameraController: CameraController;
}

interface FinalFinishesViewContextType {
	selectedPackItemId: string;
	handlePackItemClicked: (id: string) => void;
	selectedPaintProtectionItemId: string;
	handlePaintProtectionItemClicked: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function FinalFinishesView(props: FinalFinishesViewProps) {

	// get the app context and ensure it is valid.
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('AppContext must be used within the main App component');
	}

	// initial values.
	const initialPackItemId = Configuration.accentPackOption;
	const initialPaintProtectionItemId = Configuration.paintProtectionFilmOption;

	// states.
	const [selectedPackItemId, setSelectedPackItemId] = useState(initialPackItemId);
	const [selectedPaintProtectionItemId, setSelectedPaintProtectionItemId] = useState(initialPaintProtectionItemId);

	// handlers.

	const handlePaintProtectionItemClicked = (id: string) => {

		// toggle the item.
		if (id === selectedPaintProtectionItemId) {
			id = 'NONE';
		}

		// update the selected item and item name.
		setSelectedPaintProtectionItemId(id);

		// update the configuration.
		Configuration.paintProtectionFilmOption = id;
		Configuration.updateCosts();

		// n.b. no need to change camera view for paint protection film as it doesn't change the appearance of the vehicle.
	}

	const handlePackItemClicked = (id: string) => {

		// toggle the item.
		if (id === selectedPackItemId) {
			id = AccentPackOptions.None;
		}

		// ensure the windows are up when selecting an accent pack.
		context.setWindowsUp(true);
		props.exteriorConfigurator.selectSideWindowState(true);

		// update the selected item and item name.
		setSelectedPackItemId(id);

		// update the configuration.
		Configuration.accentPackOption = id;
		Configuration.updateCosts();

		// select the accent pack style on the car.
		props.exteriorConfigurator.selectAccentPackStyle(id);

		// set the camera to the accent pack viewpoint.
		props.cameraController.changeCameraView(CameraViews.AccentPackView);
	}

	// return the HTML.
	return (
		<FinalFinishesViewContext.Provider value={{ selectedPackItemId, handlePackItemClicked, selectedPaintProtectionItemId, handlePaintProtectionItemClicked }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-final-finishes">
							FINAL FINISHES
						</h2>
					</div>

					<SectionPanel
						subHeading={'PACKS'}
						modalContent={CloudData.getModalContent(selectedPackItemId, CloudData.accessoryOptions)}
					>
						<SelectedSectionItem
							itemName={getAccessoryItemName(selectedPackItemId)}
							sectionPrice={CloudData.getPrice(selectedPackItemId, CloudData.accessoryOptions, { ref: false })}
						/>
						<PackItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'PAINT PROTECTION'}
						modalContent={CloudData.getModalContent(selectedPaintProtectionItemId, CloudData.accessoryOptions)}
					>
						<SelectedSectionItem
							itemName={getAccessoryItemName(selectedPaintProtectionItemId)}
							sectionPrice={CloudData.getPrice(selectedPaintProtectionItemId, CloudData.accessoryOptions, { ref: false })}
						/>
						<PaintProtectionItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</FinalFinishesViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const FinalFinishesViewContext = createContext<FinalFinishesViewContextType | undefined>(undefined);

function PackItems() {

	// return the html.
	return(
		<ViewContentRows>
			<PackItem id={'EA001'} imageFilePath={'dark-accent-pack'} />
		</ViewContentRows>
	);
}

function PackItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(FinalFinishesViewContext);
	if (!context) {
		throw new Error('FinalFinishesViewContext must be used within a FinalFinishesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getAccessoryItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/final-finishes/${props.imageFilePath}.svg`}
			isSelected={props.id === context.selectedPackItemId}
			onItemClicked={context.handlePackItemClicked}
			isDisabled={props.isDisabled}
		/>
	);
}

function PaintProtectionItems() {

	// return the html.
	return (
		<ViewContentRows>
			<PaintProtectionItem id={'BCSF007'} imageFilePath={'paint-protection-film'} />
		</ViewContentRows>
	);
}

function PaintProtectionItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(FinalFinishesViewContext);
	if (!context) {
		throw new Error('FinalFinishesViewContext must be used within a FinalFinishesView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getAccessoryItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/final-finishes/${props.imageFilePath}.svg`}
			isSelected={props.id === context.selectedPaintProtectionItemId}
			onItemClicked={context.handlePaintProtectionItemClicked}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getAccessoryItemName(id: string): string {
	return CloudData.getName(id, CloudData.accessoryOptions);
}